import { useId, useState } from 'react';
import PropTypes from 'prop-types';
import './Input.css';

/**
 * Reusable Input component with label, error handling, and validation.
 * When type="password" a show/hide toggle is rendered automatically.
 *
 * @param {Object} props
 * @param {string} props.id - Optional input ID (generated if not provided)
 * @param {string} props.type - Input type: 'text', 'email', 'password', 'number', 'tel', 'date', etc.
 * @param {string} props.label - Input label text
 * @param {string} props.name - Input name attribute
 * @param {string} props.value - Input value
 * @param {string} props.placeholder - Placeholder text
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onBlur - Blur handler
 * @param {string} props.error - Error message to display
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {boolean} props.required - Whether input is required
 * @param {string} props.helperText - Helper text to display below input
 */
const Input = ({
  id,
  type = 'text',
  label,
  name,
  value,
  placeholder,
  onChange,
  onBlur,
  error,
  disabled = false,
  required = false,
  helperText,
  className = '',
  ...rest
}) => {
  const generatedId = useId();
  const inputId = id || `input-${name}-${generatedId}`;
  const hasError = !!error;
  const isPassword = type === 'password';

  const [showPassword, setShowPassword] = useState(false);

  const resolvedType = isPassword ? (showPassword ? 'text' : 'password') : type;

  const inputClassNames = [
    'input',
    isPassword && 'input--has-toggle',
    hasError && 'input--error',
    disabled && 'input--disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="input-wrapper">
      {label && (
        <label htmlFor={inputId} className="input-label">
          {label}
          {required && <span className="input-required">*</span>}
        </label>
      )}
      <div className={`input-field-wrap${isPassword ? ' input-field-wrap--password' : ''}`}>
        <input
          id={inputId}
          type={resolvedType}
          name={name}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          required={required}
          className={inputClassNames}
          aria-invalid={hasError}
          aria-describedby={
            hasError ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            className="input-toggle-visibility"
            onClick={() => setShowPassword(prev => !prev)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            {showPassword ? (
              // Eye-off icon
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            ) : (
              // Eye icon
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            )}
          </button>
        )}
      </div>
      {hasError && (
        <div id={`${inputId}-error`} className="input-error">
          {error}
        </div>
      )}
      {!hasError && helperText && (
        <div id={`${inputId}-helper`} className="input-helper">
          {helperText}
        </div>
      )}
    </div>
  );
};

Input.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  helperText: PropTypes.string,
  className: PropTypes.string,
};

export default Input;
